import { BASE_URL } from "../../request";
import ModuleTranslations from "./ModuleTranslations";
import { generateBase58uuid, getTranslatedDefinition } from "../utils";
import { ModuleDefinition as BookableDefinition } from "../Bookable/ModuleDefinition";
import {
  CODE_TYPE,
  DEVICE_TYPE,
  RATE_TYPE,
  RESTRICTION_TYPE,
  SHARING_TYPE,
} from "../../Common/constants";
import "material-icons/iconfont/material-icons.css";
import { IconPack } from "../../../shared/widgets/DynamicIconList/IconPack.enum";

function getRestrictionDates(restriction = "") {
  const dates = restriction.split("-");

  if (!dates[1]) {
    return restriction;
  }

  return {
    startDate: dates[0],
    endDate: dates[1],
  };
}

export function removeIconSetIdentifier(iconValue) {
  return iconValue && iconValue.split("/")[1];
}

const ModuleDefinition = {
  name: "Promotions",
  entityName: "promotions",
  restConfig: {
    baseURL: `${BASE_URL}`,
    create: {
      url: ({ propertyTicker }) => {
        return `api/property/${propertyTicker}/engine/administration/promo/${generateBase58uuid()}/create`;
      },
      method: "put",
      resetCache: true,
    },
    update: {
      url: ({ propertyTicker, id }) =>
        `api/property/${propertyTicker}/engine/administration/promo/${id}/update`,
      method: "put",
      resetCache: true,
    },
    reorder: {
      url: ({ propertyTicker, id, afterId }) =>
        `api/property/${propertyTicker}/engine/administration/promo/${id}/reorder` +
        (afterId ? `?afterId=${encodeURIComponent(afterId)}` : ""),
      method: "put",
      resetCache: true,
    },
    fetch: {
      url: ({ propertyTicker, id }) =>
        `api/property/${propertyTicker}/engine/administration/promo/${id ? id : ""}`,
      method: "get",
    },
    delete: {
      url: ({ propertyTicker, id }) =>
        `api/property/${propertyTicker}/engine/administration/promo/${id}`,
      method: "delete",
      resetCache: true,
    },
  },
  entities: [],
  renderers: {
    list: {},
    form: {},
  }, //render functions by attribute name, this overrides default widgets specified in the fields section
  translation: {
    translated: true,
    fieldName: "translations",
    displayName: "translations",
    widget: "translations",
    entityProperty: "translations",
  },
  showMenu: true,
  tabs: [
    {
      title: {
        create: "Configuración",
        edit: "Edición",
      },
    },
    {
      title: "Código de uso único",
      hideReminderMessage: true,
      hideSubmitButton: true,
    },
  ],
  allowClone: true,
  sorting: false,
  reorder: true,
  fields: {
    list: [
      ...BookableDefinition.fields.list.filter(v => v.fieldName !== "arrangement"),
      {
        fieldName: "promotionType",
        displayName: "Tipologia",
        widget: "hidden",
      },
      {
        fieldName: "sharingType",
        displayName: "Uso",
        widget: "hidden",
      },
      {
        fieldName: "group",
        displayName: "Grupo",
        width: 50,
        tooltip: "Acumulación de promociones",
      },
      {
        fieldName: `${RESTRICTION_TYPE.OPEN_TRAVEL}_value`,
        displayName: "Validez (travel w)",
        widget: "dateRange",
        multiLine: true,
        getDates: entity => getRestrictionDates(entity[`${RESTRICTION_TYPE.OPEN_TRAVEL}_value`]),
        width: 80,
      },
      {
        fieldName: `${RESTRICTION_TYPE.CLOSE_TRAVEL}_value`,
        displayName: "Bloqueos (travel w)",
        width: 200,
      },
      {
        fieldName: `${RESTRICTION_TYPE.OPEN_BOOKING}_value`,
        displayName: "Contratable (booking w)",
        widget: "dateRange",
        multiLine: true,
        getDates: entity => getRestrictionDates(entity[`${RESTRICTION_TYPE.OPEN_BOOKING}_value`]),
        width: 80,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MIN_STAY}_duration`,
        displayName: "Est. min. (noches)",
        width: 80,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MAX_STAY}_duration`,
        displayName: "Est. max. (noches)",
        width: 80,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MIN_NOTICE}_duration`,
        displayName: "Ant. Min. (d) (Release)",
        width: 80,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MAX_NOTICE}_duration`,
        displayName: "Ant. max. (d)  (Release)",
        width: 80,
      },
      {
        fieldName: RESTRICTION_TYPE.CODE,
        displayName: "Códigos",
        width: 200,
      },
      {
        fieldName: "type",
        displayName: "Uso",
        width: 80,
      },
    ],
    form: [
      ...BookableDefinition.fields.form,
      {
        translationsList: true,
        order: 61,
      },
      {
        fieldName: "rateTypeSection",
        displayName: "Tipología",
        widget: "section",
        order: 62,
      },
      {
        fieldName: "rateType",
        defaultValue: RATE_TYPE.PERCENTAGE,
        widget: "radioButtonGroup",
        options: [
          {
            ticker: RATE_TYPE.PERCENTAGE,
            name: "Descuento %",
          },
          {
            ticker: RATE_TYPE.CONSTANT,
            name: "Dto. fijo/dia",
          },
          {
            ticker: RATE_TYPE.GIVE_NIGHT,
            name: "Regalar noche/s (ej. 3x2)",
          },
          {
            ticker: RATE_TYPE.HIGHLIGHT_MESSAGE,
            name: "Mensaje destacado",
          },
        ],
        order: 63,
      },
      {
        fieldName: "ratePercentage",
        displayName: "Cantidad",
        widget: "input",
        required: true,
        order: 70,
        visible: ({ formValues }) => formValues.rateType === RATE_TYPE.PERCENTAGE,
      },
      {
        fieldName: "rateConstant",
        displayName: "Cantidad",
        widget: "input",
        required: true,
        order: 70,
        visible: ({ formValues }) => formValues.rateType === RATE_TYPE.CONSTANT,
      },
      {
        fieldName: "icon",
        displayName: "Icon",
        widget: "dynamicIconSelector",
        placeholder: "Sin icono / No icon",
        iconPack: IconPack.MATERIAL_DESIGN_ICONS,
        iconBaseProps: {
          size: 24,
        },
        visible: ({ formValues }) => formValues.rateType === RATE_TYPE.HIGHLIGHT_MESSAGE,
        order: 72,
      },
      {
        widget: "discountPeriodicity",
        order: 73,
        visible: ({ formValues }) => formValues.rateType === RATE_TYPE.GIVE_NIGHT,
      },
      {
        fieldName: "accumulationPromotions",
        openContainerText: "Ver acumulación de promociones",
        closeContainerText: "Ocultar acumulación de promociones",
        widget: "accordion",
        order: 75,
      },
      {
        fieldName: "accumulationPromotionsSection",
        className: "sectionAccordion",
        displayName: "Acumulación de promociones",
        widget: "section",
        subtitle:
          "Sólo se acumularán promos que pertenezcan a grupos diferentes, si existen varias promos aplicables de un mismo grupo, se elige aquella que tenga el mayor beneficio.",
        parent: "accumulationPromotions",
        order: 76,
      },
      {
        fieldName: "group",
        displayName: "Grupo",
        widget: "selector",
        defaultValue: "GROUP_01",
        parent: "accumulationPromotions",
        order: 77,
      },
      {
        fieldName: "stayDatesSection",
        displayName: "Fechas de estancia (travel window)",
        widget: "section",
        order: 80,
      },
      {
        fieldName: RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE,
        displayName: "Start date",
        widget: "datepicker",
        dateFormat: "YYYY-MM-DD",
        placeholder: "Introduce una fecha",
        isRange: true,
        startField: RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE,
        endField: RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE,
        selectsStart: true,
        className: "tickerInput closeButtonInside",
        order: 81,
      },
      {
        fieldName: RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE,
        displayName: "End date",
        widget: "datepicker",
        dateFormat: "YYYY-MM-DD",
        placeholder: "Introduce una fecha",
        isRange: true,
        startField: RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE,
        endField: RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE,
        selectsStart: true,
        className: "tickerInput closeButtonInside",
        order: 82,
      },
      {
        fieldName: RESTRICTION_TYPE.CLOSE_TRAVEL,
        displayName: "Bloqueo",
        widget: "checkbox",
        defaultValue: true,
        order: 83,
      },
      {
        fieldName: "codeType",
        displayName: "",
        widget: "selector",
        defaultValue: CODE_TYPE.NO_CODE,
        optionDisplay: {
          [CODE_TYPE.NO_CODE]: {
            label: "Sin código",
          },
          [CODE_TYPE.UNIVERSAL_CODE]: {
            label: "Código universal",
          },
        },
        order: 91,
      },
      {
        fieldName: RESTRICTION_TYPE.CODE,
        displayName: "",
        widget: "input",
        order: 92,
        visible: ({ formValues }) => formValues.codeType !== CODE_TYPE.NO_CODE,
      },
      {
        fieldName: "showPopup",
        displayName: "Mostrar pop up con descripción de promociones",
        widget: "singleSwitch",
        order: 93,
        visible: ({ formValues }) => formValues.codeType !== CODE_TYPE.NO_CODE,
      },
      {
        fieldName: "restrictionsSection",
        displayName: "Restricciones",
        widget: "section",
        order: 100,
      },
      {
        fieldName: RESTRICTION_TYPE.MIN_STAY,
        description: "Estancia mínima (noches)",
        widget: "checkbox",
        className: "withoutMarginPadding checkboxWeight",
        order: 101,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MIN_STAY}_duration`,
        widget: "input",
        order: 102,
        visible: ({ formValues }) => formValues[RESTRICTION_TYPE.MIN_STAY],
      },
      {
        fieldName: RESTRICTION_TYPE.MAX_STAY,
        description: "Estancia máxima (noches)",
        widget: "checkbox",
        className: "withoutMarginPadding checkboxWeight",
        order: 103,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MAX_STAY}_duration`,
        widget: "input",
        order: 104,
        visible: ({ formValues }) => formValues[RESTRICTION_TYPE.MAX_STAY],
      },
      {
        fieldName: RESTRICTION_TYPE.MIN_NOTICE,
        description: "Antelación mínima (días)",
        widget: "checkbox",
        className: "withoutMarginPadding checkboxWeight",
        order: 105,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MIN_NOTICE}_duration`,
        widget: "input",
        order: 106,
        visible: ({ formValues }) => formValues[RESTRICTION_TYPE.MIN_NOTICE],
      },
      {
        fieldName: RESTRICTION_TYPE.MAX_NOTICE,
        description: "Antelación máxima (días)",
        className: "checkboxWeight",
        widget: "checkbox",
        order: 107,
      },
      {
        fieldName: `${RESTRICTION_TYPE.MAX_NOTICE}_duration`,
        widget: "input",
        order: 108,
        visible: ({ formValues }) => formValues[RESTRICTION_TYPE.MAX_NOTICE],
      },
      {
        fieldName: "deviceSection",
        displayName: "Dispositivos habilitados",
        widget: "section",
        subtitle:
          "Esta promoción se aplicará a los usuarios conectados desde los dispositivos escogidos.",
        order: 110,
      },
      {
        fieldName: RESTRICTION_TYPE.DEVICE_TYPE,
        displayName: "",
        widget: "multicheckbox",
        defaultValue: [DEVICE_TYPE.DESKTOP, DEVICE_TYPE.TABLET, DEVICE_TYPE.MOBILE],
        options: [
          {
            ticker: DEVICE_TYPE.DESKTOP,
            name: "Desktop",
          },
          {
            ticker: DEVICE_TYPE.TABLET,
            name: "Tablet",
          },
          {
            ticker: DEVICE_TYPE.MOBILE,
            name: "Móvil",
          },
        ],
        order: 111,
      },
      {
        fieldName: "geoLocationSection",
        displayName: "Geolocation",
        widget: "section",
        subtitle:
          "This promotion will only apply to the users with an IP originating from countries marked in the included countries list",
        order: 112,
      },
      {
        fieldName: RESTRICTION_TYPE.GEOLOCATION,
        defaultValue: [],
        widget: "dualListBox",
        options: [],
        order: 115,
      },
      {
        fieldName: "sharingType",
        displayName: "Use",
        widget: "selector",
        order: 116,
        optionDisplay: {
          [SHARING_TYPE.SIMPLE]: {
            label: "Only displayed in aggregator",
          },
          [SHARING_TYPE.SHARED]: {
            label: "Shared",
          },
        },
        defaultValue: SHARING_TYPE.SIMPLE,
        visible: ({ formValues }) => formValues.appliesToInventoriesList,
      },
      {
        fieldName: "appliesToInventoriesSection",
        displayName: "Líneas de inventario",
        widget: "section",
        subtitle: "Esta promoción sólo se aplicará a las líneas inventario escogidas",
        order: 120,
      },
      {
        fieldName: "appliesToInventories",
        widget: "smartSelector",
        defaultValue: [],
        transform: ({ field, wbkData, formValues }) => ({
          ...field,
          options:
            (wbkData.inventories || []).map(inv => {
              inv.name =
                inv.accommodation.name +
                " / " +
                inv.configuration.name +
                " , " +
                inv.mealPlan.name +
                " , " +
                inv.condition.name;
              inv.filters = {
                accommodation: inv.accommodation,
                condition: inv.condition,
                configuration: inv.configuration,
                mealPlan: inv.mealPlan,
              };
              return inv;
            }) || {},
          staticFilters: {},
        }),
        filterNames: ["Accommodation", "Conditions", "Configuration", "Meal Plan"],
        order: 121,
        visible: ({ formValues }) => formValues.appliesToInventories,
      },
      {
        fieldName: "appliesToInventoriesList",
        widget: "smartSelectorList",
        defaultValue: [],
        transform: ({ field, formValues }) => ({
          ...field,
          options:
            (formValues.inventories || []).map(inventoryList => {
              return inventoryList.map(inventory => {
                inventory.name =
                  inventory.accommodation.name +
                  " / " +
                  inventory.configuration.name +
                  " , " +
                  inventory.mealPlan.name +
                  " , " +
                  inventory.condition.name;
                inventory.filters = {
                  accommodation: inventory.accommodation,
                  condition: inventory.condition,
                  configuration: inventory.configuration,
                  mealPlan: inventory.mealPlan,
                };
                return inventory;
              });
            }) || [],
          staticFilters: {},
          propertyTickers: formValues.propertyTickers || [],
          onEditByExcel: formValues.onEditByExcel,
        }),
        filterNames: ["Accommodation", "Conditions", "Configuration", "Meal Plan"],
        order: 122,
        visible: ({ formValues }) => formValues.sharingType === SHARING_TYPE.SHARED,
      },
      {
        displayName: "Booking Window",
        widget: "section",
        order: 84,
        fieldName: "bookingWindowSection",
        className: "noBorderNoMargin",
      },
      {
        fieldName: "bookingWindow",
        introductionText: [],
        widget: "nestedEntity",
        nested: true,
        entityName: "bookingWindow",
        nestedTitle: "Booking Window",
        defaultValue: [],
        order: 85,
      },
      {
        fieldName: "infoMessageChainPromo",
        widget: "simpleText",
        className: "simpleTextReminderContainer",
        order: 130,
        value: "Los cambios se aplicarán en todos los motores seleccionados",
        visible: ({ formValues }) => {
          return formValues.inventories && formValues.propertyTickers;
        },
      },
      {
        fieldName: "oneTimePromoCode",
        widget: "oneTimePromoCode",
        transform: ({ field, formValues }) => ({
          ...field,
          propertyTickers: formValues.propertyTickers || [],
        }),
        tab: 1,
        visible: ({ entity }) => entity.id,
        entityType: "DISCOUNT",
      },
      {
        fieldName: "infoMessage",
        widget: "simpleText",
        value:
          "Para activar códigos de un solo uso debes guardar una promoción primero y acceder de nuevo para editar.",
        tab: 1,
        visible: ({ entity }) => !entity.id,
      },
    ],
  },
  validation: {
    ...BookableDefinition.validation,
    [RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE]: {
      type: "Date",
      constraints: {
        compare: {
          logic: "isLesser",
          than: [RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE],
          message: "^The end date must be greater than the start date.",
          valueType: "date",
        },
      },
      relatedFields: [RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE],
    },
    [RESTRICTION_TYPE.OPEN_TRAVEL_END_DATE]: {
      type: "Date",
      constraints: {
        compare: {
          logic: "isGreater",
          than: [RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE],
          message: "^The end date must be greater than the start date and greater than today.",
          valueType: "date",
        },
      },
      relatedFields: [RESTRICTION_TYPE.OPEN_TRAVEL_START_DATE],
    },
    [`${RESTRICTION_TYPE.MIN_STAY}_duration`]: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 1,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    [`${RESTRICTION_TYPE.MAX_STAY}_duration`]: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 1,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    [`${RESTRICTION_TYPE.MIN_NOTICE}_duration`]: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 1,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    [`${RESTRICTION_TYPE.MAX_NOTICE}_duration`]: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 1,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
  },
};

export default ({ locale }) =>
  getTranslatedDefinition({ ModuleDefinition, ModuleTranslations, locale });
